import React, { useState, FC, useEffect } from 'react'
import { Button, message, Table, Modal } from 'antd'
import { UserInviteFormValues, UserInviteListItem } from '../../types'
import { inviteService } from '../../services'
import RequestError from '../../services/RequestError'
import { SearchBar, Page } from '../../components'
import CreateInviteModal from './CreateInviteModal'

import styles from './index.module.less'

const { confirm } = Modal

const Users: FC = () => {
	const [loading, setLoading] = useState<boolean>(false)
	const [invites, setInvites] = useState<UserInviteListItem[]>([])
	const [filter, setFilter] = useState<string>()
	const [isCreateModalVisible, setIsCreateModalVisible] = useState<boolean>(false)

	const searchBarButtonProps = () => {
		return {
			onClick: (): void => setIsCreateModalVisible(true),
			children: 'Add new',
			icon: 'plus'
		}
	}

	const columns = [
		{ title: 'Email', dataIndex: 'email' },
		{
			title: 'Type',
			render: (record: UserInviteListItem) =>
				record.organization_type === 'service_provider' ? 'Service Provider Administrator' : 'Reseller Administrator'
		},
		{
			title: 'Organization(s)',
			render: (record: UserInviteListItem) => record.organizations.map(org => org.name).join(', ')
		},
		{
			title: '',
			dataIndex: 'actions',
			render: (_: string, invite: UserInviteListItem) => (
				<div style={{ textAlign: 'right' }}>
					<Button
						type='danger'
						icon='delete'
						onClick={() =>
							confirm({
								title: 'Are you sure you want to delete this invite?',
								content: `${invite.email}`,
								okType: 'danger',
								okText: 'Delete',
								async onOk() {
									await deleteInvite(invite)
								}
							})
						}
					>
						Delete Invite
					</Button>
				</div>
			)
		}
	]

	const getInvites = async (): Promise<void> => {
		setLoading(true)
		const { data } = await inviteService.getAll()
		setInvites(data)
		setLoading(false)
	}

	const createUser = async (values: UserInviteFormValues): Promise<void> => {
		try {
			await inviteService.create(values)
			setIsCreateModalVisible(false)
			await getInvites()
		} catch (e) {
			const error = e as RequestError
			if (error.response?.status === 409) {
				message.warning(`${values.email} has already been invited.`)
			} else {
				message.error('Something went wrong when trying to send the invitation. Please try again.')
			}
		}
	}

	const deleteInvite = async ({ email }: UserInviteListItem) => {
		setLoading(true)

		try {
			await inviteService.delete(email)
			await getInvites()
			message.success(`Invite for ${email} has been successfully deleted`)
		} catch (error) {
			message.error('We ran into an unexpected error when attempting to delete the invite')
		} finally {
			setLoading(false)
		}
	}

	useEffect(() => {
		getInvites()
	}, [])

	return (
		<Page title='Invites'>
			<SearchBar className={styles.filter} placeholder='Filter by email' search={setFilter} buttonProps={searchBarButtonProps()} />
			<Table
				loading={loading}
				dataSource={invites.filter(invite => (filter ? invite.email.toLowerCase().includes(filter.toLowerCase()) : true))}
				rowKey='email'
				columns={columns}
			/>
			<CreateInviteModal isVisible={isCreateModalVisible} create={createUser} close={() => setIsCreateModalVisible(false)} />
		</Page>
	)
}

export default Users
